import React, { useState, useEffect } from "react";
import {
    Box,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Divider,
  CircularProgress,
  Alert
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { GoogleOAuthProvider } from '@react-oauth/google';
import GoogleLoginButton from "../../components/GoogleLoginButton";
import LinkedInLoginButton from "../../components/LinkedInLogin";
import { sendOTP, verifyOTP } from "../../services/authService";

// Import images from assets
import backgroundImage from "../../assets/images/login-bkg.png";
import loginImage from "../../assets/images/log-chat.svg";
import blackLogo from "../../assets/images/blackLogo.svg";

const Login = () => {
    const [loading, setLoading] = useState(true);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('warning');
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [formData, setFormData] = React.useState({
        email: '',
        otp: '',
    });
    // not allowed domains for login
    const not_allowed_domains = ['gmail', 'hotmail', 'yahoo', 'outlook', 'aol'];

    useEffect(() => {
        const checkUserInfo = () => {
            // check if userInfo is in localStorage
            const userInfo = typeof window !== 'undefined' ? localStorage.getItem('userInfo') : null;
            if (userInfo) {
                try {
                    window.location.href = '/dashboard';
                } catch (error) {
                    console.error('Login page - error parsing userInfo:', error);
                }
            }
            setLoading(false);
        };

        checkUserInfo();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: e.target.type === 'checkbox' ? checked : value
        }));
    };

    // handle submit for email login
    const handleSubmit = async (e) => {
        const email = formData.email;
        const domain = email.split('@')[1].split('.')[0];
        if (not_allowed_domains.includes(domain)) {
            setShowAlert(true);
            setAlertSeverity('error');
            setAlertMessage('Please use a official email address');
            return;
        }
        
        setOtpLoading(true);
        try {
            const response = await sendOTP(email, 'login');
            if (response.status === 'success') {
                setOtpSent(true);
                setAlertSeverity('success');
                setAlertMessage('OTP sent successfully! Please check your email.');
                setShowAlert(true);
            } else {
                setAlertSeverity('error');
                setAlertMessage(response.message || 'Failed to send OTP');
                setShowAlert(true);
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            setAlertSeverity('error');
            setAlertMessage(error.response?.data?.message || 'Failed to send OTP. Please try again.');
            setShowAlert(true);
        } finally {
            setOtpLoading(false);
        }
    };

    // handle submit for otp login
    const handleOtpSubmit = async (e) => {
        if (!formData.otp || formData.otp.length !== 6) {
            setAlertSeverity('error');
            setAlertMessage('Please enter a valid 6-digit OTP');
            setShowAlert(true);
            return;
        }
    
        setOtpLoading(true);
        try {
            const response = await verifyOTP(formData.email, formData.otp, 'login');
            if (response.status === 'success') {
                // Store user data in localStorage
                localStorage.setItem('userInfo', JSON.stringify(response.data));
                // Show success message
                setAlertSeverity('success');
                setAlertMessage('Login successful! Redirecting to dashboard...');
                setShowAlert(true);
                // Redirect to dashboard after a short delay
                setTimeout(() => {
                    window.location.href = '/dashboard';
                }, 1500);
            } else {
                setAlertSeverity('error');
                setAlertMessage(response.message || 'OTP verification failed');
                setShowAlert(true);
            }
        } catch (error) {
            setAlertSeverity('error');
            setAlertMessage(error.response?.data?.message || 'Failed to verify OTP. Please try again.');
            setShowAlert(true);
        } finally {
            setOtpLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
    
        <Box sx={{ minHeight: "100vh", width: "100vw", display: "flex", flexDirection: { xs: "column", md: "row" } }} >
            {/* Left Side - Login Box */}
            <Box display="flex" flexDirection="column" sx={{px: 4, backgroundColor: grey[100], minHeight: "100vh", width: {xs: "100vw", md: "50vw"}}}>
        
                <Box sx={{m:5, display: 'flex', justifyContent: {xs: 'center', md: 'flex-start'}}}>
                    <img src={blackLogo} alt="getLista" style={{ height: 40 }} />
                </Box>
        
                <Box flexGrow={1} margin={{ xs: 0, sm: 1, md: 2 }} display="flex" alignItems="center" justifyContent="center" >
                    <Paper elevation={3} sx={{ p: { xs: 2, sm: 5 }, width: "100%", maxWidth: 500, borderRadius: 5, background: "white", position: "relative" }}>
            
                        {otpLoading && (
                            <Box sx={{position: 'absolute',backgroundColor: 'rgba(255, 255, 255, 0.8)',display: 'flex',alignItems: 'center',justifyContent: 'center',zIndex: 1000,borderRadius: 5}}>
                                <CircularProgress />
                            </Box>
                        )}

                        {showAlert && (
                            <Alert severity={alertSeverity} sx={{ mt: 2, mb: 2, width: '100%' }} onClose={() => {setShowAlert(false)}}>
                                {alertMessage}
                            </Alert>
                        )}

                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 2, textAlign: "center", letterSpacing: "-0.03em", color: "black"}}>Sign in to Getlista</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "black", mb: 3, textAlign: "center" }}>Welcome back! Please enter your details.</Typography>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: 'center' }}>
                            <GoogleOAuthProvider clientId="523077414487-nqglvq3pva83tsup3oknal9m6fcdistk.apps.googleusercontent.com">
                             {/* <GoogleOAuthProvider clientId="241614144702-i5tlbf9svklkf6i2cevl9o1trisb1ojl.apps.googleusercontent.com"> */}
                                <GoogleLoginButton />
                            </GoogleOAuthProvider>

                            <LinkedInLoginButton />
                        </Box>
            
            <Box sx={{ my: 2, textAlign: "center", color: "#000000", fontWeight: 600 }} >
              or
            </Box>
            
            <Box>
              <TextField 
                label="Official Email" 
                type="email" 
                name="email" 
                fullWidth 
                value={formData.email} 
                onChange={handleInputChange} 
                required 
                disabled={otpSent}
                sx={{ borderRadius: 5, backgroundColor: "white" }} 
              />
              
              {otpSent && (
                <TextField 
                  label="Enter OTP" 
                  type="text" 
                  name="otp" 
                  fullWidth 
                  value={formData.otp} 
                  onChange={handleInputChange} 
                  required 
                  sx={{ borderRadius: 5, backgroundColor: "white", mt: 2 }} 
                  placeholder="Enter 6-digit OTP"
                  inputProps={{ maxLength: 6 }}
                />
              )}
              
              <Button 
                // type="submit" 
                variant="contained" 
                fullWidth
                disabled={otpLoading}
                sx={{ 
                  py: 1.5, 
                  fontWeight: 700, 
                  fontSize: "1rem", 
                  borderRadius: 2, 
                  background: "#1ce484", 
                  boxShadow: "none", 
                  mt: 2, 
                  mb: 2, 
                  textTransform: "none", 
                  "&:hover": { 
                    background: "#20ec84",
                  }
                }}
                onClick={otpSent ? handleOtpSubmit : handleSubmit}
              >
                {otpLoading ? 'Sending OTP...' : (otpSent ? 'Verify OTP' : 'Send OTP')}
              </Button>
              
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" sx={{ textAlign: "center", color: "black" }}>
              Don&apos;t have an account?{" "}
              <Link href="/register" underline="hover" sx={{ color: "#3b82f6", fontWeight: 600 }}>
                Sign up
              </Link>
            </Typography>
          </Paper>
        </Box>
      </Box>
      
      {/* Right Side - Image (Hidden on xs and sm) */}
      <Box sx={{ flex: 1, display: { xs: "none", md: "flex" }, background: `url(${backgroundImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', alignItems: 'center', justifyContent: 'center'}}>
        <img src={loginImage} alt="Login Visual" style={{ maxWidth: '80%', maxHeight: '80vh', objectFit: 'contain'}}/>
      </Box>

    </Box>
);
};

export default Login;